import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Badge,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  MarkEmailRead as MarkAllReadIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  RequestPage as FundsRequestIcon,
  EmojiEvents as WelcomeIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import CircleNotificationsOutlinedIcon from '@mui/icons-material/CircleNotificationsOutlined';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import NotificationDialog from './NotificationDialog';

const NotificationsDropdown = ({ 
  anchorEl, 
  open, 
  onClose, 
  unreadCount,
  onUnreadCountChange 
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Dialog state
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const getNotificationIcon = (type) => {
    const icons = {
      'GROUP_REQUEST': <GroupIcon color="primary" />,
      'GROUP_UPDATE': <GroupIcon color="info" />,
      'FUNDS_REQUEST': <FundsRequestIcon color="warning" />,
      'CAMPAIGN_VERIFICATION_REQUEST': <FundsRequestIcon color="success" />,
      'WELCOME': <WelcomeIcon color="success" />
    };
    return icons[type] || <CircleNotificationsOutlinedIcon />;
  };

  // Fetch notifications with infinite scroll
  const fetchNotifications = async (pageNumber = 0, isInitialLoad = false) => {
    if (loading && !isInitialLoad) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/v1/api/notifications?page=${pageNumber}&size=10&sort=ASC`);

      const newNotifications = response.data.content;
      const totalPages = response.data.page.totalPages;
      
      if (isInitialLoad) {
        setNotifications(newNotifications);
      } else {
        setNotifications(prev => [...prev, ...newNotifications]);
      }
      
      setHasMore(pageNumber < totalPages - 1);
      setError(null);
      
      // Update unread count from the first page load
      if (isInitialLoad && onUnreadCountChange) {
        const unreadCount = newNotifications.filter(n => !n.read).length;
        onUnreadCountChange(unreadCount);
      }
    } catch (err) {
      setError('Failed to fetch notifications');
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  // Infinite scroll handler
  const handleScroll = useCallback((event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    
    // Load more when 80% scrolled
    if (scrollHeight - scrollTop <= clientHeight * 1.2 && 
        hasMore && 
        !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchNotifications(nextPage);
    }
  }, [page, hasMore, loading]);

  const markAsRead = async (id) => {
    try {
      await api.patch(`/v1/api/notifications/${id}/read`);
      setNotifications(notifications.map(n =>
        n.id === id ? { ...n, read: true } : n
      ));
      if (onUnreadCountChange) {
        onUnreadCountChange(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.patch('/v1/api/notifications/mark-all-read');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      if (onUnreadCountChange) {
        onUnreadCountChange(0);
      }
      // Refetch to get updated read status
      fetchNotifications(0, true);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/v1/api/notifications/${id}`);
      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== id));
      // Update unread count if needed
      const deletedNotification = notifications.find(n => n.id === id);
      if (deletedNotification && !deletedNotification.read && onUnreadCountChange) {
        onUnreadCountChange(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.read) {
      await markAsRead(notification.id);
    }
    
    // Set selected notification and open dialog
    setSelectedNotification(notification);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedNotification(null);
  };

  const handleNotificationUpdate = (notificationId, updates) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, ...updates } : n)
    );
  };

  const handleNotificationDelete = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    if (onUnreadCountChange) {
      onUnreadCountChange(prev => Math.max(0, prev - 1));
    }
  };

  // Reset and load notifications when dropdown opens
  useEffect(() => {
    if (open) {
      setPage(0);
      setHasMore(true);
      setNotifications([]);
      setError(null);
      fetchNotifications(0, true);
    }
  }, [open]);

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        MenuListProps={{
          sx: { padding: 0 }
        }}
        PaperProps={{
          sx: {
            width: isSmallScreen ? '100vw' : 400,
            maxWidth: isSmallScreen ? '100vw' : '90vw',
            height: isSmallScreen ? '100vh' : 'auto',
            maxHeight: isSmallScreen ? '100vh' : '80vh',
            overflow: 'hidden',
            margin: isSmallScreen ? 0 : undefined,
            borderRadius: isSmallScreen ? 0 : undefined,
            position: isSmallScreen ? 'fixed' : 'relative',
            top: isSmallScreen ? '0 !important' : undefined,
            left: isSmallScreen ? '0 !important' : undefined,
            padding: 0
          }
        }}
      >
        {/* Notifications Header */}
        <Box sx={{ 
          p: 1, 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: 'primary.main',
          color: 'primary.contrastText'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Notifications
              {unreadCount > 0 && (
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  sx={{ ml: 1.3 }}
                />
              )}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  startIcon={<MarkAllReadIcon />}
                  onClick={markAllAsRead}
                  disabled={loading}
                  sx={{ 
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Mark all read
                </Button>
              )}
              <IconButton
                onClick={onClose}
                sx={{
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Button
            size="small"
            onClick={() => {
              onClose();
              navigate('/notifications');
            }}
            sx={{ 
              textTransform: 'none',
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            View all notifications
          </Button>
        </Box>

        {/* Notifications Content with Infinite Scroll */}
        <Box 
          sx={{ 
            maxHeight: isSmallScreen ? 'calc(100vh - 160px)' : 400, 
            overflow: 'auto',
            position: 'relative'
          }}
          onScroll={handleScroll}
        >
          {error && (
            <Alert severity="error" sx={{ m: 1 }}>
              {error}
            </Alert>
          )}

          {notifications.length === 0 && !loading ? (
            <Box textAlign="center" p={3}>
              <Typography variant="body2" color="text.secondary">
                No notifications found
              </Typography>
            </Box>
          ) : (
            <List sx={{ width: '100%', p: 0 }}>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      backgroundColor: notification.read ? 'inherit' : 'action.hover',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.selected'
                      }
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'background.paper', width: 32, height: 32 }}>
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Typography
                            fontWeight={notification.read ? 'normal' : 'bold'}
                            variant="body2"
                            sx={{ flex: 1, mr: 1 }}
                          >
                            {notification.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          color="text.primary"
                          sx={{ 
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {notification.message}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              
              {/* Loading indicator for infinite scroll */}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              
              {/* End of notifications message */}
              {!hasMore && notifications.length > 0 && (
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    No more notifications
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </Box>
      </Menu>

      {/* Notification Dialog */}
      <NotificationDialog
        key={selectedNotification?.id}
        open={dialogOpen}
        onClose={handleDialogClose}
        notification={selectedNotification}
        onNotificationUpdate={handleNotificationUpdate}
        onNotificationDelete={handleNotificationDelete}
      />
    </>
  );
};

export default NotificationsDropdown;